/* Mobile purchase controls use the theme's original variant and cart handling. */
if (!customElements.get('hss-mobile-purchase')) {
  customElements.define('hss-mobile-purchase', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      if (!this.product || this.ready) return;
      this.ready = true;
      this.mobile = matchMedia('(max-width: 749px)');
      this.mainButton = this.product.querySelector('.product-form__submit');
      this.form = this.mainButton?.form;
      if (!this.form) return;
      this.sticky = this.querySelector('.hss-sticky-purchase');
      this.stickyButton = this.querySelector('.hss-sticky-button');
      this.stickyButton.setAttribute('aria-haspopup', 'dialog');
      // Keep fixed positioning outside the theme's animated product container.
      document.body.append(this.sticky);
      this.explicit = !!this.product.dataset.hssExplicitVariant && new URLSearchParams(location.search).has('variant');
      this.chosenSize = this.explicit ? this.selectedSize() : null;
      this.originalGuide = [...this.product.querySelectorAll('modal-opener')].find(el => /size guide/i.test(el.textContent));
      this.originalGuide?.classList.add('hss-original-size-guide');
      this.picker = this.product.querySelector('variant-selects');
      this.pending = false;
      this.onChange = event => {
        if (!event.target.closest('variant-selects')) return;
        this.pending = true;
        if (event.target.closest('[data-hss-size-group]')) this.chosenSize = event.target.value;
        this.queueSync();
      };
      this.onSubmit = event => {
        if (!this.needsSize() && !this.pending) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.needsSize()) this.focusSize();
      };
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.updateSticky(); });
      };
      this.onBreakpoint = () => this.sync();
      this.product.addEventListener('change', this.onChange, true);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.stickyButton.addEventListener('click', () => {
        if (this.needsSize()) this.focusSize();
        else if (!this.mainButton.disabled && this.mainButton.getAttribute('aria-disabled') !== 'true') this.mainButton.click();
      });
      this.observer = new MutationObserver(() => this.queueSync());
      this.observer.observe(this.product.querySelector('.product__info-container'), { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      this.overlayObserver = new MutationObserver(() => this.updateSticky());
      this.overlayObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      this.layoutObserver = new ResizeObserver(this.onScroll);
      this.layoutObserver.observe(this.mainButton);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      window.visualViewport?.addEventListener('resize', this.onScroll);
      this.mobile.addEventListener('change', this.onBreakpoint);
      this.sync();
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.overlayObserver?.disconnect();
      this.layoutObserver?.disconnect(); this.previewObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.sticky?.remove();
      this.product?.removeEventListener('change', this.onChange, true);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.visualViewport?.removeEventListener('resize', this.onScroll);
      this.mobile?.removeEventListener('change', this.onBreakpoint);
      document.body.classList.remove('hss-has-sticky-purchase');
    }
    sizeGroup() {
      return [...this.product.querySelectorAll('variant-selects .product-form__input')].find(group => {
        const input = group.querySelector('input, select');
        return /^(size|waist)(?:-|\[|$)/i.test(input?.name || '') || /^options\[(size|waist)\]$/i.test(input?.name || '');
      });
    }
    selectedSize() {
      const group = this.sizeGroup();
      return group?.querySelector('input:checked')?.value || group?.querySelector('select')?.value || null;
    }
    variant() {
      try { return JSON.parse(this.product.querySelector('[data-selected-variant]')?.textContent || 'null'); }
      catch { return null; }
    }
    needsSize() { return this.mobile.matches && !!this.sizeGroup() && !this.chosenSize; }
    queueSync() {
      if (this.queued) return;
      this.queued = true;
      queueMicrotask(() => { this.queued = false; if (this.isConnected) this.sync(); });
    }
    text(element, value) { if (element && element.textContent !== value) element.textContent = value; }
    sync() {
      const picker = this.product.querySelector('variant-selects');
      if (picker !== this.picker) {
        this.picker = picker; this.pending = false;
        if (this.chosenSize && this.selectedSize() !== this.chosenSize) this.chosenSize = null;
      }
      const group = this.sizeGroup();
      if (group) {
        group.dataset.hssSizeGroup = '';
        const heading = group.querySelector('legend, .form__label');
        heading?.classList.add('hss-size-heading');
        if (heading && this.originalGuide && !heading.querySelector('.hss-inline-size-guide')) {
          const guide = document.createElement('button');
          guide.type = 'button'; guide.className = 'hss-inline-size-guide';
          guide.textContent = 'Size guide'; guide.setAttribute('aria-haspopup', 'dialog');
          guide.addEventListener('click', () => document.querySelector(this.originalGuide.dataset.modal)?.show(guide));
          heading.append(guide);
        }
        if (!group.querySelector('.hss-size-help')) {
          const help = document.createElement('p'); help.className = 'hss-size-help';
          help.id = `${this.product.id}-size-help`; help.tabIndex = -1;
          help.textContent = 'Choose your size. Compare the garment measurements for the best fit.';
          group.append(help);
        }
        this.text(group.querySelector('.hss-size-help'), this.needsSize() ? 'Choose your size. Compare the garment measurements for the best fit.' : 'Compare the garment measurements for the best fit.');
        if (this.needsSize()) {
          const url = new URL(location.href);
          if (url.searchParams.has('variant')) { url.searchParams.delete('variant'); history.replaceState(history.state, '', url); }
          group.querySelectorAll('input:checked').forEach(input => { input.checked = false; });
          const select = group.querySelector('select');
          if (select) {
            if (!select.querySelector('[data-hss-placeholder]')) {
              const placeholder = new Option('Choose size', ''); placeholder.dataset.hssPlaceholder = ''; placeholder.disabled = true; select.prepend(placeholder);
            }
            select.value = '';
          }
        } else if (!this.mobile.matches && !this.selectedSize()) {
          const variant = this.variant();
          const groups = [...this.product.querySelectorAll('variant-selects .product-form__input')];
          const value = variant?.options?.[groups.indexOf(group)];
          group.querySelectorAll('input').forEach(input => { input.checked = input.value === value; });
          const select = group.querySelector('select'); if (select && value) select.value = value;
        }
      }
      // Accelerated checkout must not bypass size selection or a pending variant.
      this.product.toggleAttribute('data-hss-needs-size', this.needsSize() || this.pending);
      const label = this.mainButton.querySelector('span');
      if (this.needsSize() && !this.pending) {
        // The capture submit guard remains in force while native variants refresh.
        if (this.mainButton.disabled) this.mainButton.disabled = false;
        this.text(label, 'Choose size');
      } else if (!this.pending && label?.textContent.trim() === 'Choose size') {
        const variant = this.variant();
        this.mainButton.disabled = !variant?.available;
        this.text(label, variant?.available ? window.variantStrings.addToCart : window.variantStrings.soldOut);
      }
      this.updateStickyContent();
      this.updateSticky();
    }
    focusSize() {
      const group = this.sizeGroup();
      group?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      group?.querySelector('.hss-size-help')?.focus({ preventScroll: true });
    }
    updateSticky() {
      if (!this.sticky) return;
      const overlay = document.body.classList.contains('overflow-hidden') || document.querySelector('modal-dialog[open], cart-drawer.active, details[open] > .menu-drawer');
      const show = this.mainButton.getBoundingClientRect().bottom < 0 && !overlay;
      // Shopify's review toolbar covers the viewport bottom, including on desktop.
      const preview = document.getElementById('PBarNextFrameWrapper');
      if (preview && preview !== this.previewWrapper) {
        this.previewWrapper = preview;
        this.layoutObserver?.observe(preview);
        this.previewObserver?.disconnect();
        this.previewObserver = new MutationObserver(this.onScroll);
        this.previewObserver.observe(preview, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      }
      const bounds = preview?.getBoundingClientRect();
      const offset = bounds && bounds.height > 0 && bounds.top < innerHeight && bounds.bottom > 0
        ? Math.max(0, innerHeight - bounds.top) : 0;
      const offsetValue = `${Math.ceil(offset)}px`;
      if (this.sticky.style.getPropertyValue('--hss-preview-offset') !== offsetValue) this.sticky.style.setProperty('--hss-preview-offset', offsetValue);
      if (this.sticky.hidden === show) this.sticky.hidden = !show;
      if (document.body.classList.contains('hss-has-sticky-purchase') !== show) document.body.classList.toggle('hss-has-sticky-purchase', show);
    }
    // Variant and price content changes on product updates, not on every scroll frame.
    updateStickyContent() {
      if (!this.sticky) return;
      const price = this.product.querySelector('.price:not(.price--on-sale) .price-item--regular, .price--on-sale .price-item--sale');
      if (price) this.text(this.sticky.querySelector('.hss-sticky-price'), price.textContent.trim());
      this.text(this.sticky.querySelector('.hss-sticky-choice'), this.needsSize() ? 'Choose your size' : (this.variant()?.options || []).join(' / '));
      this.text(this.stickyButton, this.needsSize() ? 'Choose size' : this.mainButton.textContent.trim());
      const disabled = this.pending || !this.needsSize() && (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true');
      if (this.stickyButton.disabled !== disabled) this.stickyButton.disabled = disabled;
    }
  });
}
